import React from 'react';
import ReactDOM from 'react-dom/client';
import { createRoot } from "react-dom/client";
import "./index.css";

// ✅ CORREÇÃO DEFINITIVA: Expor React globalmente ANTES de importar App
// Isso garante que @tanstack/react-query pode usar React.createContext
if (typeof window !== 'undefined') {
  (window as any).React = React;
  (window as any).ReactDOM = ReactDOM;
  console.log('[main.tsx] ✅ React exposto globalmente');
}

// ✅ Importar App DEPOIS de garantir que React está disponível
import App from "./App.tsx";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ✅ DETECTOR DE CHUNKS FALTANDO - adicionar ANTES do render
window.addEventListener('error', (event) => {
  const errorMsg = event.message || '';
  const isChunkError = 
    errorMsg.includes('Loading chunk') || 
    errorMsg.includes('Failed to fetch dynamically imported module') ||
    errorMsg.includes('Failed to fetch') && errorMsg.includes('.js');
  
  if (isChunkError) {
    console.error('[main.tsx] ❌ ERRO DE CHUNK DETECTADO:', errorMsg);
    
    // ✅ Criar UI de erro usando DOM seguro (sem innerHTML)
    const errorContainer = document.createElement('div');
    errorContainer.style.cssText = 'position: fixed; inset: 0; background: #141414; color: #f2f2f2; display: flex; align-items: center; justify-content: center; z-index: 99999; padding: 20px; font-family: system-ui, -apple-system, sans-serif;';
    
    const contentDiv = document.createElement('div');
    contentDiv.style.cssText = 'max-width: 500px; text-align: center; width: 100%;';
    
    const iconDiv = document.createElement('div');
    iconDiv.style.cssText = 'font-size: 64px; margin-bottom: 24px;';
    iconDiv.textContent = '⚠️';
    
    const title = document.createElement('h2');
    title.style.cssText = 'font-size: 28px; margin-bottom: 16px; font-weight: 600; color: #f2f2f2;';
    title.textContent = 'Erro ao Carregar';
    
    const description = document.createElement('p');
    description.style.cssText = 'color: #a0a0a0; margin-bottom: 24px; line-height: 1.6; font-size: 16px;';
    description.textContent = 'Alguns arquivos não puderam ser carregados. Isso pode acontecer devido a conexão instável ou cache corrompido.';
    
    const button = document.createElement('button');
    button.style.cssText = 'background: #3b82f6; color: white; padding: 14px 28px; border: none; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; width: 100%; transition: background 0.2s;';
    button.textContent = '🔄 Limpar Cache e Recarregar';
    button.onclick = () => {
      console.log('[main.tsx] 🧹 Limpando cache via botão...');
      localStorage.clear();
      sessionStorage.clear();
      if ('caches' in window) {
        caches.keys().then(names => {
          console.log('[main.tsx] 🗑️ Deletando', names.length, 'caches');
          return Promise.all(names.map(name => caches.delete(name)));
        }).then(() => {
          console.log('[main.tsx] ✅ Caches limpos');
          if ('serviceWorker' in navigator) {
            return navigator.serviceWorker.getRegistrations().then(regs => {
              console.log('[main.tsx] 🗑️ Desregistrando', regs.length, 'SWs');
              return Promise.all(regs.map(reg => reg.unregister()));
            });
          }
        }).then(() => {
          console.log('[main.tsx] ✅ SWs removidos');
          console.log('[main.tsx] 🔄 Redirecionando...');
          setTimeout(() => { location.href = '/'; }, 500);
        });
      } else {
        setTimeout(() => { location.href = '/'; }, 500);
      }
    };
    button.onmouseover = () => { button.style.background = '#2563eb'; };
    button.onmouseout = () => { button.style.background = '#3b82f6'; };
    
    contentDiv.appendChild(iconDiv);
    contentDiv.appendChild(title);
    contentDiv.appendChild(description);
    contentDiv.appendChild(button);
    errorContainer.appendChild(contentDiv);
    document.body.appendChild(errorContainer);
    
    // Prevenir propagação do erro
    event.preventDefault();
    return false;
  }
}, true);

// ✅ Capturar erros críticos de inicialização
try {
  const rootElement = document.getElementById("root");
  
  if (!rootElement) {
    throw new Error("Elemento #root não encontrado no DOM");
  }
  
  console.log('[main.tsx] 🚀 Iniciando renderização...', {
    hasRoot: !!rootElement,
    env: import.meta.env.MODE,
    hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
    timestamp: new Date().toISOString()
  });
  
  createRoot(rootElement).render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  );
  
  console.log('[main.tsx] ✅ Renderização iniciada com sucesso');
} catch (error) {
  console.error('[main.tsx] ❌ ERRO FATAL DE INICIALIZAÇÃO:', error);
  
  // ✅ Fallback UI se React falhar completamente (usando DOM seguro)
  document.body.textContent = ''; // Limpar body
  
  const container = document.createElement('div');
  container.style.cssText = 'display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; background: #141414; color: #f2f2f2; font-family: system-ui, -apple-system, sans-serif;';
  
  const content = document.createElement('div');
  content.style.cssText = 'text-align: center; max-width: 500px;';
  
  const spinner = document.createElement('div');
  spinner.style.cssText = 'width: 64px; height: 64px; margin: 0 auto 24px; border: 4px solid #ef4444; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;';
  
  const title = document.createElement('h1');
  title.style.cssText = 'font-size: 28px; margin-bottom: 16px; color: #ef4444; font-weight: 600;';
  title.textContent = 'Erro Fatal';
  
  const message = document.createElement('p');
  message.style.cssText = 'color: #a0a0a0; margin-bottom: 24px; line-height: 1.6;';
  message.textContent = 'Não foi possível inicializar a aplicação VYRA.';
  
  const details = document.createElement('details');
  details.style.cssText = 'background: #1f1f1f; padding: 16px; border-radius: 8px; text-align: left; margin-bottom: 24px;';
  
  const summary = document.createElement('summary');
  summary.style.cssText = 'cursor: pointer; font-weight: 600; margin-bottom: 12px; color: #f2f2f2;';
  summary.textContent = 'Detalhes do erro';
  
  const pre = document.createElement('pre');
  pre.style.cssText = 'font-size: 12px; overflow: auto; color: #888; white-space: pre-wrap; word-break: break-word;';
  pre.textContent = error instanceof Error ? error.stack || error.message : String(error);
  
  details.appendChild(summary);
  details.appendChild(pre);
  
  const reloadBtn = document.createElement('button');
  reloadBtn.style.cssText = 'background: #3b82f6; color: white; padding: 12px 24px; border: none; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; width: 100%; margin-bottom: 12px;';
  reloadBtn.textContent = 'Recarregar Página';
  reloadBtn.onclick = () => location.reload();
  
  const clearBtn = document.createElement('button');
  clearBtn.style.cssText = 'background: transparent; color: #a0a0a0; padding: 12px 24px; border: 1px solid #333; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; width: 100%;';
  clearBtn.textContent = 'Limpar Cache e Reiniciar';
  clearBtn.onclick = () => {
    localStorage.clear();
    sessionStorage.clear();
    location.href = '/';
  };
  
  const style = document.createElement('style');
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  
  content.appendChild(spinner);
  content.appendChild(title);
  content.appendChild(message);
  content.appendChild(details);
  content.appendChild(reloadBtn);
  content.appendChild(clearBtn);
  container.appendChild(content);
  document.head.appendChild(style);
  document.body.appendChild(container);
}
